import { useLayoutEffect } from "react";

const SITE_NAME = "Spike Hunter Online";
const SITE_ORIGIN = "https://spikehunter.online";
const DEFAULT_TITLE = "Spike Hunter Online";
const DEFAULT_DESCRIPTION =
  "Spike Hunter Online — Trading Intelligence for Boom & Crash markets.";

function upsertMeta(attrName, attrValue, content) {
  let el = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
  const preexisting = Boolean(el);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return { el, preexisting };
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const preexisting = Boolean(el);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return { el, preexisting };
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return el;
}

function removeIfPresent(el) {
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/**
 * Dependency-free head manager for the blog. Tags are written synchronously
 * (useLayoutEffect, before paint) so react-snap's DOM snapshot always
 * captures the final title/meta/JSON-LD state rather than a pre-render one.
 */
export default function SEOHead({
  title,
  description,
  path,
  type = "website",
  article,
  faqs,
  noIndex = false,
}) {
  useLayoutEffect(() => {
    const canonicalUrl = `${SITE_ORIGIN}${path || "/"}`;
    const resolvedTitle = title || DEFAULT_TITLE;
    const resolvedDescription = description || DEFAULT_DESCRIPTION;

    document.title = resolvedTitle;

    const { el: descriptionEl } = upsertMeta("name", "description", resolvedDescription);
    const { el: canonicalEl, preexisting: canonicalPreexisting } = upsertLink(
      "canonical",
      canonicalUrl
    );
    const { el: ogTitleEl, preexisting: ogTitlePreexisting } = upsertMeta(
      "property",
      "og:title",
      resolvedTitle
    );
    const { el: ogDescEl, preexisting: ogDescPreexisting } = upsertMeta(
      "property",
      "og:description",
      resolvedDescription
    );
    const { el: ogTypeEl, preexisting: ogTypePreexisting } = upsertMeta(
      "property",
      "og:type",
      type
    );
    const { el: ogUrlEl, preexisting: ogUrlPreexisting } = upsertMeta(
      "property",
      "og:url",
      canonicalUrl
    );
    const { el: ogSiteEl, preexisting: ogSitePreexisting } = upsertMeta(
      "property",
      "og:site_name",
      SITE_NAME
    );

    let robotsResult = null;
    if (noIndex) {
      robotsResult = upsertMeta("name", "robots", "noindex, follow");
    }

    let articleScriptEl = null;
    if (article) {
      articleScriptEl = upsertJsonLd("seo-jsonld-article", {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.description,
        datePublished: article.publishDate,
        dateModified: article.publishDate,
        author: { "@type": "Organization", name: SITE_NAME },
        publisher: {
          "@type": "Organization",
          name: SITE_NAME,
          logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/logo512.png` },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      });
    }

    let faqScriptEl = null;
    if (faqs && faqs.length > 0) {
      faqScriptEl = upsertJsonLd("seo-jsonld-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      });
    }

    return () => {
      document.title = DEFAULT_TITLE;
      descriptionEl.setAttribute("content", DEFAULT_DESCRIPTION);
      if (!canonicalPreexisting) removeIfPresent(canonicalEl);
      if (!ogTitlePreexisting) removeIfPresent(ogTitleEl);
      if (!ogDescPreexisting) removeIfPresent(ogDescEl);
      if (!ogTypePreexisting) removeIfPresent(ogTypeEl);
      if (!ogUrlPreexisting) removeIfPresent(ogUrlEl);
      if (!ogSitePreexisting) removeIfPresent(ogSiteEl);
      if (robotsResult && !robotsResult.preexisting) removeIfPresent(robotsResult.el);
      removeIfPresent(articleScriptEl);
      removeIfPresent(faqScriptEl);
    };
  }, [title, description, path, type, article, faqs, noIndex]);

  return null;
}
